import {
    TRADER_CELL_WIDTH,
    TRADER_COLUMNS,
    TRADER_COLUMN_GAP,
    TRADER_ROW_PADDING,
    type TraderColumn,
    type TraderColumnGroup,
} from '../components/traderColumns';

// Which trader columns a viewer sees (columns spec 4.2). Auto shows, by
// priority, every column that fits the measured width; any manual change
// turns the current view into a custom set kept in localStorage. Only the
// hidden keys are stored, so a column added later shows up in a custom set.

export const COLUMNS_STORAGE_KEY = 'irm.traderSearch.columns';
// The width the automatic set assumes before the result box is measured.
export const UNMEASURED_WIDTH = 1024;

export type TraderColumnsPref =
    | { mode: 'auto' }
    | { mode: 'custom'; hidden: string[] };

export const AUTO_PREF: TraderColumnsPref = { mode: 'auto' };

export type ColumnsChange =
    | { kind: 'hide' | 'show'; key: string }
    | { kind: 'hideGroup' | 'showGroup'; group: TraderColumnGroup };

const KNOWN = new Set(TRADER_COLUMNS.map(c => c.key));
const BY_PRIORITY = [...TRADER_COLUMNS].sort((a, b) => a.priority - b.priority);

export function fitColumns(width: number): Set<string> {
    const room = width - TRADER_CELL_WIDTH - TRADER_ROW_PADDING;
    const keys = new Set<string>();
    let used = 0;
    for (const column of BY_PRIORITY) {
        const need = column.width + TRADER_COLUMN_GAP;
        if (used + need <= room) {
            keys.add(column.key);
            used += need;
        }
    }
    // On a narrow screen the table scrolls sideways rather than showing no
    // value at all.
    if (keys.size === 0) keys.add(BY_PRIORITY[0].key);
    return keys;
}

function shownKeys(pref: TraderColumnsPref, width: number): Set<string> {
    if (pref.mode === 'auto') return fitColumns(width);
    const hidden = new Set(pref.hidden);
    return new Set(TRADER_COLUMNS.map(c => c.key).filter(k => !hidden.has(k)));
}

export function visibleColumns(
    pref: TraderColumnsPref,
    width: number
): TraderColumn[] {
    const keys = shownKeys(pref, width);
    return TRADER_COLUMNS.filter(c => keys.has(c.key));
}

export function editPref(
    pref: TraderColumnsPref,
    width: number,
    change: ColumnsChange
): TraderColumnsPref {
    const shown = shownKeys(pref, width);
    const hidden = new Set(
        TRADER_COLUMNS.map(c => c.key).filter(k => !shown.has(k))
    );
    const group = (g: TraderColumnGroup) =>
        TRADER_COLUMNS.filter(c => c.group === g).map(c => c.key);
    switch (change.kind) {
        case 'hide':
            hidden.add(change.key);
            break;
        case 'show':
            hidden.delete(change.key);
            break;
        case 'hideGroup':
            group(change.group).forEach(k => hidden.add(k));
            break;
        case 'showGroup':
            group(change.group).forEach(k => hidden.delete(k));
            break;
    }
    return {
        mode: 'custom',
        hidden: TRADER_COLUMNS.map(c => c.key).filter(k => hidden.has(k)),
    };
}

function browserStorage(): Storage | null {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
}

export function readPref(
    storage: Storage | null = browserStorage()
): TraderColumnsPref {
    try {
        const raw = storage?.getItem(COLUMNS_STORAGE_KEY);
        if (!raw) return AUTO_PREF;
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return AUTO_PREF;
        const { v, mode, hidden } = parsed as Record<string, unknown>;
        if (v !== 1) return AUTO_PREF;
        if (mode === 'custom' && Array.isArray(hidden)) {
            return {
                mode: 'custom',
                hidden: hidden.filter(
                    (k): k is string => typeof k === 'string' && KNOWN.has(k)
                ),
            };
        }
        return AUTO_PREF;
    } catch {
        return AUTO_PREF;
    }
}

export function writePref(
    pref: TraderColumnsPref,
    storage: Storage | null = browserStorage()
): void {
    try {
        storage?.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify({ v: 1, ...pref })
        );
    } catch {
        // Private mode or a full quota: the choice lasts for this page only.
    }
}

export function columnsBadge(
    mode: TraderColumnsPref['mode'],
    count: number
): string {
    return `${mode === 'auto' ? 'Auto · ' : ''}${count}/${TRADER_COLUMNS.length}`;
}
